import React from 'react';

import type { IntlShape } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import type { Command } from '@atlaskit/editor-common/types';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { AICommandIcon, AISummarizeIcon } from '@atlaskit/editor-toolbar';
import MagicWandIcon from '@atlaskit/icon/core/magic-wand';

import { createOpenAIModalCommand } from '../../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../../types/types';
import { summarizeSmartlink } from '../../prebuilt/config-items/prompts/summarize-smartlink/summarize-smartlink';

export type SmartLinkDropdownItem = {
	icon: React.ReactNode;
	label: string;
	onClick: () => void;
	testId?: string;
};

export const getSmartLinkDropdownItems = ({
	aiGlobalOptIn,
	intl,
	nodeType,
	smartlinkUrl,
	testIdPrefix,
	view,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
	intl: IntlShape;
	nodeType: NodeType;
	smartlinkUrl: string;
	testIdPrefix: string;
	view: EditorView;
}): SmartLinkDropdownItem[] => {
	const dispatchCommand = (cmd: Command) => {
		cmd(view.state, view.dispatch, view);
	};

	// this is a temporary placeholder handler until we have the individual actions built out.
	// once we have the actual actions, we'll replace this handler with the specific handlers for each action
	const summarize = () => {
		const openAIModalCommand = createOpenAIModalCommand({
			state: view.state,
			configItem: summarizeSmartlink({ smartlinkUrl }), // this will be dynamic based on the action
			lastTriggeredFrom: 'floatingToolbar',
			triggerMethod: 'click',
			aiGlobalOptIn,
			locale: intl.locale,
			triggeredFor: nodeType,
		});
		dispatchCommand(openAIModalCommand);
	};

	// these items are placeholders until we have the individual actions built out.
	// once we have the actual actions, we'll replace the onClick handlers to trigger those specific actions
	return [
		{
			icon: <AISummarizeIcon label="" size="small" />,
			label: intl.formatMessage(aiMessages.summarizeToolbarDropdownIconTitle),
			testId: `${testIdPrefix}-smartlink-dropdown-summarize`,
			onClick: summarize,
		},
		{
			icon: <AICommandIcon label="" size="small" />,
			label: intl.formatMessage(aiMessages.improveWritingToolbarIconTitle),
			testId: `${testIdPrefix}-smartlink-dropdown-improve-writing`,
			onClick: summarize,
		},
		{
			icon: <MagicWandIcon label="" size="small" />,
			label: intl.formatMessage(aiMessages.makeShorterToolbarDropdownIconTitle),
			testId: `${testIdPrefix}-smartlink-dropdown-magic`,
			onClick: summarize,
		},
	];
};
